import type {
  CreateMemberGroupRequest,
  MemberGroup,
  UpdateMemberGroupRequest
} from '@library/types/memberGroups';
import { Input, TextArea } from '@library/components/inputs';
import { isNil, pick, reject } from 'ramda';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Concrete } from '@bo-types/common';
import { FormActionButtons } from '@library/components/buttons';
import { Modal } from '@library/components/modals/Modal';
import { PrizepicksSwitch } from '@library/components/switches';

interface EditMemberGroupModalSave extends UpdateMemberGroupRequest {
  id: number;
}

const memberGroupFields = [
  'name',
  'description',
  'vip',
  'prevent_lineups'
] as const;

type EditingMemberGroupType = Pick<
  Concrete<MemberGroup>,
  (typeof memberGroupFields)[number]
>;

const defaultMemberGroup: EditingMemberGroupType = {
  name: '',
  description: '',
  vip: false,
  prevent_lineups: false
};

export const CreateEditMemberGroupModal = ({
  memberGroup,
  isOpen,
  onCancelModal,
  onSaveCreate,
  onSaveEdit
}: {
  memberGroup: MemberGroup | undefined;
  isOpen: boolean;
  onCancelModal: () => void;
  onSaveCreate: (request: CreateMemberGroupRequest) => Promise<void>;
  onSaveEdit: (request: EditMemberGroupModalSave) => Promise<void>;
}) => {
  const [isSubmitting, setSubmitting] = useState(false);

  const [editingMemberGroup, setEditingMemberGroup] =
    useState<EditingMemberGroupType>(defaultMemberGroup);

  const resetFields = useCallback(() => {
    setEditingMemberGroup(defaultMemberGroup);
  }, []);

  const hasChanges = useMemo(
    () =>
      !memberGroup ||
      Object.entries(editingMemberGroup).some(([key, value]) => {
        return memberGroup[key as keyof MemberGroup] !== value;
      }),
    [editingMemberGroup, memberGroup]
  );

  useEffect(() => {
    if (memberGroup) {
      setEditingMemberGroup({
        ...defaultMemberGroup,
        ...reject(isNil, pick(memberGroupFields, memberGroup))
      });
    } else {
      resetFields();
    }
  }, [memberGroup, resetFields]);

  const saveDisabled = useMemo<boolean>(
    () => isSubmitting || !editingMemberGroup.name || !hasChanges,
    [isSubmitting, editingMemberGroup.name, hasChanges]
  );

  const handleCancelModal = useCallback(() => {
    resetFields();
    onCancelModal();
  }, [onCancelModal, resetFields]);

  const handleSubmit = useCallback(() => {
    const saveMemberGroup = async () => {
      setSubmitting(true);

      if (memberGroup) {
        await onSaveEdit({ id: memberGroup.id, ...editingMemberGroup });
      } else {
        await onSaveCreate(editingMemberGroup);
      }

      resetFields();

      setSubmitting(false);
    };

    // eslint-disable-next-line no-void
    void saveMemberGroup();
  }, [editingMemberGroup, memberGroup, onSaveCreate, onSaveEdit, resetFields]);

  const setName = useCallback(
    (name: EditingMemberGroupType['name']) => {
      setEditingMemberGroup({ ...editingMemberGroup, name });
    },
    [editingMemberGroup]
  );

  const setDescription = useCallback(
    (description: EditingMemberGroupType['description']) => {
      setEditingMemberGroup({ ...editingMemberGroup, description });
    },
    [editingMemberGroup]
  );

  const setVIP = useCallback(
    (vip: EditingMemberGroupType['vip']) => {
      setEditingMemberGroup({ ...editingMemberGroup, vip });
    },
    [editingMemberGroup]
  );

  const setPreventLineups = useCallback(
    (prevent_lineups: EditingMemberGroupType['prevent_lineups']) => {
      setEditingMemberGroup({ ...editingMemberGroup, prevent_lineups });
    },
    [editingMemberGroup]
  );

  return (
    <Modal isOpen={isOpen} onCancelModal={handleCancelModal}>
      <div className="bg-soFresh-100 border-soFresh-140 relative mx-auto w-[45rem] flex-col rounded-md border">
        <div className="text-soClean-100 border-soFresh-140 flex h-10 items-center justify-between rounded-t border-b border-solid px-4 font-medium">
          <span>{memberGroup ? 'Edit' : 'Create new'} member group</span>
        </div>
        <div className="flex max-h-[70vh] flex-col gap-y-2 overflow-y-auto p-4">
          <Input
            labelName="Name"
            value={editingMemberGroup.name}
            onValueChange={setName}
          />

          <TextArea
            className="h-20 min-h-[80px] resize-none"
            labelName="Description"
            value={editingMemberGroup.description}
            onValueChange={setDescription}
          />

          <label
            htmlFor="is-vip"
            className="text-soClean-100 rounded-md text-xs uppercase leading-6"
          >
            Is VIP?
          </label>
          <PrizepicksSwitch
            id="is-vip"
            testId="is-vip"
            active={editingMemberGroup.vip}
            handleChange={setVIP}
          />

          <label
            htmlFor="prevent-lineups"
            className="text-soClean-100 rounded-md text-xs uppercase leading-6"
          >
            Prevent lineups?
          </label>
          <PrizepicksSwitch
            id="prevent-lineups"
            testId="prevent-lineups"
            active={editingMemberGroup.prevent_lineups}
            handleChange={setPreventLineups}
          />
        </div>

        <FormActionButtons
          primary={{ onClick: handleSubmit, disabled: saveDisabled }}
          secondary={{ onClick: handleCancelModal }}
        />
      </div>
    </Modal>
  );
};
